import * as vp from 'vopay';
import * as Yup from 'yup';
import { useTranslation } from 'react-i18next';
import { FieldData } from '../../Form/types';
import { SubAccountTransferForm } from './types';
import { ClientData } from '../../../providers/ClientData.provider';

type AccountTransferRowsProp = {
  clientData: ClientData;
  showingMore: boolean;
  defaultBank: vp.DefaultBankAccountList | undefined;
  parentAccount: {
    AccountID: string;
    AccountName: string;
  };
};

export type BasicOptions = {
  label: string;
  value: string;
  isVisible?: boolean;
};

const { Select, TextField, Tooltip } = vp;

export const buildTransferFundRows = ({
  clientData,
  showingMore,
  defaultBank,
  parentAccount,
}: AccountTransferRowsProp): FieldData<SubAccountTransferForm>[] => {
  const { t } = useTranslation('translations', { keyPrefix: 'SubAccounts.TransferFunds.buildTransferFundRows' });
  const { TransactionLabelOverrideEnabled, Country, USDEFTCollectEnabled } = clientData;
  const CurrencyOptions: vp.SelectOption<BasicOptions>[] = [
    { label: 'CAD', value: 'CAD', isVisible: Country === 'CA' },
    {
      label: 'USD',
      value: 'USD',
      isVisible: Country !== 'CA' || (Country === 'CA' && vp.toBool(USDEFTCollectEnabled)),
    },
  ];
  const TransferOptions: vp.SelectOption<BasicOptions>[] = [
    { label: t('transferOptions.fundTransfer'), value: 'fundTransfer' },
  ];

  return [
    {
      label: () => t('labels.TransferTo'),
      fieldName: 'RecipientAccountID',
      getInputState: () => 'required',
      showField: () => true,
      serializeField: (values: SubAccountTransferForm) => values.RecipientAccountID,
      renderField: ({ getFormikData }) => {
        const fieldData = getFormikData<string>({});
        return (
          <Select
            version="2"
            placeholder={t('placeholders.TransferTo') || ''}
            options={[
              {
                label: parentAccount.AccountName,
                value: parentAccount.AccountID,
              },
            ]}
            {...fieldData}
            disabled
          />
        );
      },
      validate: () => Yup.mixed().required(t('validation.required') || ''),
    },
    {
      label: () => t('labels.TransferType'),
      fieldName: 'TransferType',
      getInputState: () => 'required',
      showField: () => true,
      serializeField: (values: SubAccountTransferForm) => values.TransferType,
      renderField: ({ getFormikData }) => {
        const fieldData = getFormikData<string>({});
        return (
          <Select
            version="2"
            placeholder={t('placeholders.TransferType') || ''}
            options={TransferOptions}
            {...fieldData}
            disabled
          />
        );
      },
      validate: () => Yup.mixed().required(t('validation.required') || ''),
    },
    {
      label: () => t('labels.Account'),
      fieldName: 'DefaultOperationalBankAccount',
      getInputState: () => 'required',
      showField: () => true,
      serializeField: (values: SubAccountTransferForm) => values.DefaultOperationalBankAccount,
      tooltip: () => (
        <Tooltip
          information={
            <div>
              {t('tooltip.changeDefaultBankAccount')}{' '}
              <a href="/operational-accounts">{t('tooltip.operationalAccountsLink')}</a>
            </div>
          }
          position="top"
          width="394px"
        />
      ),
      renderField: ({ getFormikData }) => {
        const fieldData = getFormikData<string>({});
        return (
          <Select
            version="2"
            placeholder={t('placeholders.Account') || ''}
            options={[
              {
                label: `${defaultBank?.AccountHolderName || ''} ${vp.getMaskedNumber(
                  defaultBank?.AccountNumber || '',
                )} `,
                value: defaultBank?.Token,
              },
            ]}
            {...fieldData}
            disabled
          />
        );
      },
      validate: () => Yup.mixed().required(t('validation.required') || ''),
    },
    {
      label: () => t('labels.Currency'),
      fieldName: 'Currency',
      getInputState: () => 'required',
      showField: () => true,
      serializeField: (values: SubAccountTransferForm) => values.Currency,
      renderField: ({ getFormikData }) => {
        const fieldData = getFormikData<string>({});
        return (
          <Select
            version="2"
            placeholder={t('placeholders.Currency') || ''}
            options={CurrencyOptions.filter((option) => option.isVisible)}
            {...fieldData}
          />
        );
      },
      validate: () => Yup.mixed().required(t('validation.required') || ''),
    },
    {
      label: () => t('labels.Amount'),
      fieldName: 'Amount',
      getInputState: () => 'required',
      showField: () => true,
      serializeField: (values: SubAccountTransferForm) => vp.sanitizeToNumeric(values.Amount),
      renderField: ({ getFormikData }) => {
        const fieldData = getFormikData<string>({});
        return (
          <TextField
            variant="outlineShadow"
            preset="currency"
            placeholder={t('placeholders.Amount') || ''}
            {...fieldData}
          />
        );
      },
      validate: () =>
        Yup.string()
          .test('amount-validation', t('validation.amountValidation') || '', (value) => {
            const amountValidation = Number(vp.sanitizeToNumeric(value)) <= 0.0;
            return !amountValidation;
          })
          .required(t('validation.required') || ''),
    },
    {
      label: () => t('labels.ClientReferenceNumber'),
      fieldName: 'ClientReferenceNumber',
      getInputState: () => 'optional',
      showField: () => showingMore,
      serializeField: (values: SubAccountTransferForm) => values.ClientReferenceNumber,
      renderField: ({ getFormikData }) => {
        const fieldData = getFormikData<string>({});
        return <TextField variant="outlineShadow" placeholder="" {...fieldData} />;
      },
      validate: () => Yup.string(),
    },
    {
      label: () => t('labels.Notes'),
      fieldName: 'Notes',
      getInputState: () => 'optional',
      showField: () => showingMore,
      serializeField: (values: SubAccountTransferForm) => values.Notes,
      renderField: ({ getFormikData }) => {
        const fieldData = getFormikData<string>({});
        return <TextField variant="outlineShadow" placeholder="" {...fieldData} />;
      },
    },
    {
      label: () => t('labels.TransactionLabel'),
      fieldName: 'TransactionLabel',
      getInputState: () => 'optional',
      showField: () => vp.toBool(TransactionLabelOverrideEnabled) && showingMore,
      serializeField: (values: SubAccountTransferForm) => values.TransactionLabel,
      renderField: ({ getFormikData }) => {
        const fieldData = getFormikData<string>({});
        return <TextField variant="outlineShadow" placeholder="" {...fieldData} />;
      },
    },
  ];
};
